import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Maximize2, Minimize2, ChevronLeft, ChevronRight, 
  Play, Pause, Volume2, VolumeX, RotateCcw, Info, MapPin 
} from 'lucide-react';
import { Button } from '../ui/button';

// Simple CSS-based 360° viewer that works without external dependencies
const PanoramaViewer = ({ 
  images = [], 
  currentIndex = 0, 
  onClose, 
  isFullscreen = false,
  onToggleFullscreen,
  propertyTitle = "Virtual Tour"
}) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [autoRotate, setAutoRotate] = useState(true);
  const [showInfo, setShowInfo] = useState(true);
  const [zoom, setZoom] = useState(1);
  
  const currentImage = images[activeIndex] || {};

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate || isDragging) return;
    
    const interval = setInterval(() => {
      setRotation(prev => ({
        ...prev,
        y: prev.y + 0.3
      }));
    }, 50);
    
    return () => clearInterval(interval);
  }, [autoRotate, isDragging]);

  // Mouse/Touch handlers for manual rotation
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setAutoRotate(false);
    setStartPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;
    
    setRotation(prev => ({
      x: Math.max(-60, Math.min(60, prev.x - deltaY * 0.3)),
      y: prev.y + deltaX * 0.3
    }));
    
    setStartPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setAutoRotate(false);
    setStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.touches[0].clientX - startPos.x;
    const deltaY = e.touches[0].clientY - startPos.y;
    
    setRotation(prev => ({
      x: Math.max(-60, Math.min(60, prev.x - deltaY * 0.3)),
      y: prev.y + deltaX * 0.3
    }));
    
    setStartPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const newZoom = zoom + (e.deltaY > 0 ? -0.1 : 0.1);
    setZoom(Math.max(0.5, Math.min(2, newZoom)));
  };

  const nextRoom = () => {
    setActiveIndex(prev => (prev + 1) % images.length);
    setRotation({ x: 0, y: 0 });
  };

  const prevRoom = () => {
    setActiveIndex(prev => (prev - 1 + images.length) % images.length);
    setRotation({ x: 0, y: 0 });
  };

  const resetView = () => {
    setRotation({ x: 0, y: 0 });
    setZoom(1);
    setAutoRotate(true);
  };

  return (
    <div 
      className={`${isFullscreen ? 'fixed inset-0 z-[100]' : 'relative w-full h-full'} bg-black`}
      ref={containerRef}
    >
      {/* 360° Viewer */}
      <div 
        className="absolute inset-0 overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        onWheel={handleWheel}
      >
        <div 
          className="w-full h-full transition-transform duration-100"
          style={{
            transform: `scale(${zoom}) rotateX(${rotation.x}deg)`,
            transformOrigin: 'center center'
          }}
        >
          <div 
            className="w-[400%] h-full flex transition-transform duration-100"
            style={{
              transform: `translateX(${-rotation.y * 0.5}px)`,
            }}
          >
            <img 
              src={currentImage.url}
              alt={currentImage.name || 'Virtual Tour'}
              className="h-full object-cover select-none"
              style={{ minWidth: '100%' }}
              draggable={false}
            />
            <img 
              src={currentImage.url}
              alt={currentImage.name || 'Virtual Tour'}
              className="h-full object-cover select-none"
              style={{ minWidth: '100%' }}
              draggable={false}
            />
            <img 
              src={currentImage.url}
              alt={currentImage.name || 'Virtual Tour'}
              className="h-full object-cover select-none"
              style={{ minWidth: '100%' }}
              draggable={false}
            />
            <img 
              src={currentImage.url}
              alt={currentImage.name || 'Virtual Tour'}
              className="h-full object-cover select-none"
              style={{ minWidth: '100%' }}
              draggable={false}
            />
          </div>
        </div>
        
        {/* Vignette overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          boxShadow: 'inset 0 0 150px 30px rgba(0,0,0,0.6)'
        }} />
      </div>

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start z-10 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-gold" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{propertyTitle}</h3>
            <p className="text-white/60 text-sm">{currentImage.name || `Room ${activeIndex + 1}`}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="bg-black/40 hover:bg-black/60 text-white"
            onClick={() => setShowInfo(!showInfo)}
          >
            <Info className="w-5 h-5" />
          </Button>
          {onToggleFullscreen && (
            <Button
              variant="ghost"
              size="icon"
              className="bg-black/40 hover:bg-black/60 text-white"
              onClick={onToggleFullscreen}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="bg-black/40 hover:bg-black/60 text-white"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Room Info Panel */}
      <AnimatePresence>
        {showInfo && currentImage.description && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="absolute left-4 top-24 glass-card p-4 max-w-xs z-10"
          >
            <h4 className="font-semibold text-white mb-2">{currentImage.name}</h4>
            <p className="text-white/70 text-sm">{currentImage.description}</p>
            {currentImage.highlights && (
              <div className="mt-3 flex flex-wrap gap-2">
                {currentImage.highlights.map((h, i) => (
                  <span key={i} className="text-xs bg-gold/20 text-gold px-2 py-1 rounded">
                    {h}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={prevRoom}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-gold/80 flex items-center justify-center transition-all z-10 group"
          >
            <ChevronLeft className="w-6 h-6 text-white group-hover:text-black" />
          </button>
          <button
            onClick={nextRoom}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 hover:bg-gold/80 flex items-center justify-center transition-all z-10 group"
          >
            <ChevronRight className="w-6 h-6 text-white group-hover:text-black" />
          </button>
        </>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-10">
        {/* Room Thumbnails */}
        {images.length > 1 && (
          <div className="flex justify-center gap-2 mb-4">
            {images.map((img, idx) => (
              <button
                key={`hotspot-${idx}`}
                onClick={() => {
                  setActiveIndex(idx);
                  setRotation({ x: 0, y: 0 });
                }}
                className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === activeIndex ? 'border-gold scale-110' : 'border-white/20 opacity-60 hover:opacity-100'
                }`}
              >
                <img 
                  src={img.thumbnail || img.url} 
                  alt={img.name || `Room ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Control Buttons */}
        <div className="flex justify-center items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className={`${autoRotate ? 'bg-gold text-black' : 'bg-black/40 text-white'} hover:bg-gold hover:text-black`}
            onClick={() => setAutoRotate(!autoRotate)}
          >
            {autoRotate ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {autoRotate ? 'Pause' : 'Auto-rotate'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="bg-black/40 text-white hover:bg-gold hover:text-black"
            onClick={resetView}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset View
          </Button>
        </div>

        {/* Instructions */}
        <p className="text-center text-white/50 text-xs mt-3">
          Drag to look around • Scroll to zoom • Click rooms to navigate
        </p>
      </div>
    </div>
  );
};

export default PanoramaViewer;
